import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Dialog,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import "./VersionHistoryModal.scss";
import Label from "../../../../components/Label/Label";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { getVersionHistoryLogs } from "../../../../redux/visualization/actions";
import Loader from "../../../../components/Loader/loader";
import CustomizedSnackbars from "../../../../components/Snackbars/CustomizedSnackbars";

// Converts "lower_control" -> "Lower Control"
const formatKey = (key = "") =>
  key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase());

// Parse JSON string if value is stringified array/object
const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;

  const trimmed = value.trim();

  if (
    (trimmed.startsWith("[") && trimmed.endsWith("]")) ||
    (trimmed.startsWith("{") && trimmed.endsWith("}"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  }

  return value;
};

const renderPrimitive = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  if (typeof value === "boolean") {
    return value ? "TRUE" : "FALSE";
  }

  return String(value);
};

const formatValue = (rawValue) => {
  const value = parseJsonValue(rawValue);

  if (value === null || value === undefined || value === "") {
    return "--";
  }

  /* ------------------------------------------------------------------ */
  /* Array                                                               */
  /* ------------------------------------------------------------------ */
  if (Array.isArray(value)) {
    if (!value.length) return "--";

    // Array of objects (Specification / Control Limit Sets etc.)
    if (
      value.every(
        (item) => item && typeof item === "object" && !Array.isArray(item),
      )
    ) {
      return (
        <Stack spacing={1}>
          {value.map((item, index) => (
            <Box key={index} className="vh-info-card">
              {Object.entries(item).map(([key, val]) => (
                <Box key={key} className="vh-info-row">
                  <Typography className="vh-info-key">
                    {formatKey(key)}
                  </Typography>

                  <Typography className="vh-info-value">
                    {renderPrimitive(parseJsonValue(val))}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </Stack>
      );
    }

    // Array of strings/numbers
    return (
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
        {value.map((item, index) => (
          <Chip
            key={index}
            label={renderPrimitive(item)}
            size="small"
            className="vh-value-chip"
          />
        ))}
      </Stack>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Object                                                              */
  /* ------------------------------------------------------------------ */
  if (typeof value === "object") {
    return (
      <Box className="vh-info-card">
        {Object.entries(value).map(([key, val]) => (
          <Box key={key} className="vh-info-row">
            <Typography className="vh-info-key">{formatKey(key)}</Typography>

            <Typography className="vh-info-value">
              {renderPrimitive(parseJsonValue(val))}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Comma separated batch ids                                            */
  /* ------------------------------------------------------------------ */
  if (typeof value === "string" && value.includes(",") && value.length > 25) {
    return (
      <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.5}>
        {value.split(",").map((item, index) => (
          <Chip
            key={index}
            label={item.trim()}
            size="small"
            className="vh-value-chip"
          />
        ))}
      </Stack>
    );
  }

  return renderPrimitive(value);
};

const formatVersion = (value) => {
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    (typeof value === "object" && Object.keys(value).length === 0)
  ) {
    return "--";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const StatusBadge = ({ status }) => {
  if (status === "Chart Added" || status === "Created") {
    return <Chip label="Added New" size="small" className="vh-status-added" />;
  }

  if (status === "Chart Deleted" || status === "Deleted") {
    return <Chip label="Deleted" size="small" className="vh-status-deleted" />;
  }

  return null;
};

const VersionPair = ({ group }) => (
  <Stack direction="row" alignItems="stretch" className="vh-version-pair">
    <Box className="vh-version-card">
      <Label
        text={`Version ${formatVersion(group?.fromVersion)}`}
        fontSize="14px !important"
        fontWeight="600 !important"
        color="#0b4b99"
      />

      <Label
        text={`Created on: ${group?.updatedAt || "--"}`}
        fontSize="11px"
        color="#535862"
      />
    </Box>

    <Box className="vh-version-arrow">
      <ArrowForwardIcon />
    </Box>

    <Box className="vh-version-card">
      <Label
        text={`Version ${formatVersion(group?.toVersion)}`}
        fontSize="14px !important"
        fontWeight="600 !important"
        color="#0b4b99"
      />

      <Label
        text={`Created on: ${group?.updatedAt || "--"}`}
        fontSize="11px"
        color="#535862"
      />
    </Box>
  </Stack>
);

const VersionHistoryModal = ({
  open,
  onClose,
  visualizationId,
  version,
  visualizationName,
}) => {
  const dispatch = useDispatch();

  const versionHistoryData = useSelector(
    (state) => state?.viz?.versionHistoryData || {},
  );

  const versionHistory = versionHistoryData?.data?.versionHistory || [];

  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedCharts, setExpandedCharts] = useState({});

  const [filters, setFilters] = useState({
    from: null,
    to: null,
    fieldPath: "",
  });

  const [err, setErr] = useState(false);
  const [message, setMessage] = useState("");

  // Expand first version group when API data is received
  // useEffect(() => {
  //   if (
  //     versionHistory?.length > 0 &&
  //     Object.keys(expandedGroups).length === 0
  //   ) {
  //     setExpandedGroups({
  //       [versionHistory[0]?.version]: true,
  //     });
  //   }
  // }, [versionHistory, expandedGroups]);

  // Get version history from API
  useEffect(() => {
    if (open) {
      dispatch(
        getVersionHistoryLogs({
          viz_id: visualizationId,
          user_id: localStorage.getItem("userid"),
        }),
      );
    }
  }, [open, dispatch, visualizationId, version]);

  // API error handling
  useEffect(() => {
    if (versionHistoryData?.error === true) {
      setMessage(versionHistoryData?.message || "Something went wrong");
      setErr(true);
    } else {
      setMessage("");
      setErr(false);
    }
  }, [versionHistoryData]);

  // Get unique field paths from all versions
  const fieldOptions = useMemo(() => {
    const fields = new Set();

    versionHistory?.forEach((group) => {
      group?.changes?.forEach((chart) => {
        chart?.fields?.forEach((field) => {
          if (field?.fieldPath) {
            fields.add(field.fieldPath);
          }
        });
      });
    });

    return [...fields].sort();
  }, [versionHistory]);

  // Filter versions based on date range and field path
  const filteredVersionHistory = useMemo(() => {
    return versionHistory?.filter((group) => {
      // Filter by date range
      if (filters.from || filters.to) {
        const groupDate = new Date(group?.updatedAt);

        if (filters.from && groupDate < filters.from) {
          return false;
        }

        if (filters.to) {
          const toDate = new Date(filters.to);
          toDate.setHours(23, 59, 59, 999); // Include entire day
          if (groupDate > toDate) {
            return false;
          }
        }
      }

      // If no field filter, include the version
      if (!filters.fieldPath) {
        return true;
      }

      // If field filter is applied, check if version contains that field
      const hasField = group?.changes?.some((chart) =>
        chart?.fields?.some((field) => field?.fieldPath === filters.fieldPath),
      );

      return hasField;
    });
  }, [versionHistory, filters]);

  // Filter charts within a group based on field path
  const filteredCharts = (group) => {
    const charts = group?.changes || [];

    if (!filters.fieldPath) {
      return charts;
    }

    return charts.filter((chart) =>
      chart?.fields?.some((field) => field?.fieldPath === filters.fieldPath),
    );
  };

  // Filter fields within a chart based on field path
  const filteredFields = (chart) => {
    const fields = chart?.fields || [];

    if (!filters.fieldPath) {
      return fields;
    }

    return fields.filter((field) => field?.fieldPath === filters.fieldPath);
  };

  const popperModifiers = [
    {
      name: "flip",
      options: {
        fallbackPlacements: [
          "top-start",
          "top-end",
          "bottom-start",
          "bottom-end",
        ],
      },
    },
    {
      name: "preventOverflow",
      options: {
        boundary: "viewport",
        padding: 8,
      },
    },
  ];

  const handleDateChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFieldPathChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      fieldPath: value,
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      from: null,
      to: null,
      fieldPath: "",
    });
  };

  const toggleGroup = (versionId) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [versionId]: !prev[versionId],
    }));
  };

  const toggleChart = (groupVersion, chartUuid) => {
    const key = `${groupVersion}-${chartUuid}`;

    setExpandedCharts((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const getChartExpanded = (group, chart) => {
    const key = `${group?.version}-${chart?.chartUuid}`;

    if (Object.prototype.hasOwnProperty.call(expandedCharts, key)) {
      return expandedCharts[key];
    }

    return false;
  };

  const isFilterActive = filters.from || filters.to || filters.fieldPath;

  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} fullWidth>
      <Box className="version-history-modal">
        {/* Header */}
        <Box className="vh-modal-header">
          <Label
            text={`Version History :- ${visualizationName}`}
            fontWeight="500"
            fontSize="14px"
          />{" "}
          <IconButton
            size="small"
            onClick={onClose}
            className="vh-close-button"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Filter Bar - Top Level */}
        <Box
          sx={{
            padding: "8px",
            // borderBottom: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: "10px",
            textAlign: "center",
          }}
        >
          {/* <Typography
            sx={{
              fontSize: "14px",
              // fontWeight: "600",
              // marginBottom: "12px",
              // color: "#333",
            }}
          >
            Filters
          </Typography> */}

          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-end"
            flexWrap="wrap"
          >
            {/* Date Range Picker */}
            <Box sx={{ display: "flex", gap: 1 }}>
              {[
                ["from", "From Date"],
                ["to", "To Date"],
              ].map(([key, placeholder]) => (
                <DatePicker
                  // size="small"
                  key={key}
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={50}
                  portalId="datepicker-portal"
                  popperPlacement="auto"
                  popperModifiers={popperModifiers}
                  popperClassName="custom-datepicker-popper"
                  selected={filters?.[key]}
                  onChange={(date) => handleDateChange(key, date)}
                  selectsStart={key === "from"}
                  selectsEnd={key === "to"}
                  startDate={filters?.from}
                  endDate={filters?.to}
                  minDate={
                    key === "to" && filters?.from ? filters.from : undefined
                  }
                  maxDate={
                    key === "from"
                      ? filters?.to
                        ? filters.to
                        : new Date()
                      : new Date()
                  }
                  placeholderText={placeholder}
                  dateFormat="dd-MMM-yyyy"
                  fixedHeight
                  renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    changeYear,
                    prevMonthButtonDisabled,
                    nextMonthButtonDisabled,
                  }) => (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 1.5,
                        py: 1,
                        bgcolor: "primary.main",
                        color: "common.white",
                        borderRadius: "8px 8px 0 0",
                      }}
                    >
                      <IconButton
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                        sx={{
                          color: "common.white",
                          p: 0.5,
                        }}
                        size="small"
                      >
                        <ArrowBackIosIcon fontSize="small" />
                      </IconButton>

                      <Box
                        sx={{
                          mx: 1,
                          fontWeight: 500,
                          cursor: "default",
                          width: 80,
                          textAlign: "center",
                        }}
                      >
                        <Label
                          text={date.toLocaleString("default", {
                            month: "long",
                          })}
                          color="#ffff"
                          fontSize={16}
                          fontWeight={600}
                        />
                      </Box>

                      <Select
                        value={date.getFullYear()}
                        onChange={(e) => changeYear(Number(e.target.value))}
                        size="small"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        MenuProps={{
                          sx: {
                            zIndex: 10000,
                          },
                        }}
                        sx={{
                          fontSize: 13,
                          color: "text.primary",
                          bgcolor: "background.paper",
                          borderRadius: 1,
                        }}
                      >
                        {Array.from(
                          {
                            length: 50,
                          },
                          (_, i) => new Date().getFullYear() - i,
                        ).map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>

                      <IconButton
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                        sx={{
                          color: "common.white",
                          p: 0.5,
                        }}
                        size="small"
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                  customInput={
                    <TextField
                      size="small"
                      fullWidth
                      sx={{ width: "150px" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarTodayIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                />
              ))}
            </Box>

            {/* Field Path Select */}
            <Select
              size="small"
              displayEmpty
              value={filters.fieldPath}
              onChange={(e) => handleFieldPathChange(e.target.value)}
              sx={{
                width: "150px",
                backgroundColor: "white",
              }}
              renderValue={(value) =>
                value || (
                  <Typography sx={{ color: "#999", fontSize: "14px" }}>
                    Select Field Path
                  </Typography>
                )
              }
            >
              <MenuItem value="">All Fields</MenuItem>

              {fieldOptions?.map((field) => (
                <MenuItem key={field} value={field}>
                  {field}
                </MenuItem>
              ))}
            </Select>

            {/* Reset Button */}
            {/* {isFilterActive && ( */}
            <Button
              disabled={isFilterActive ? false : true}
              variant="outlined"
              size="large"
              onClick={handleResetFilters}
              sx={{
                textTransform: "none",
                fontSize: "12px",
              }}
            >
              Reset Filters
            </Button>
            {/* )} */}
          </Stack>

          {/* Active Filters Display */}
          {/* {isFilterActive && (
            <Stack direction="row" spacing={1} sx={{ marginTop: "10px" }}>
              {filters.from && (
                <Chip
                  label={`From: ${filters.from.toLocaleDateString()}`}
                  size="small"
                  onDelete={() => handleDateChange("from", null)}
                />
              )}
              {filters.to && (
                <Chip
                  label={`To: ${filters.to.toLocaleDateString()}`}
                  size="small"
                  onDelete={() => handleDateChange("to", null)}
                />
              )}
              {filters.fieldPath && (
                <Chip
                  label={`Field: ${filters.fieldPath}`}
                  size="small"
                  onDelete={() => handleFieldPathChange("")}
                />
              )}
            </Stack>
          )} */}
        </Box>

        {versionHistoryData?.loader && <Loader />}

        {/* Scrollable content */}
        <Box className="vh-modal-content">
          {filteredVersionHistory?.length === 0 &&
          !versionHistoryData?.loader ? (
            <Box className="no-data-container">
              <Label
                text={
                  versionHistory?.length === 0
                    ? "No version history available"
                    : "No versions match the selected filters"
                }
                variant="outlined"
                color="info"
              />
            </Box>
          ) : (
            filteredVersionHistory?.map((group) => {
              const isGroupExpanded = Boolean(expandedGroups[group?.version]);
              const chartsToDisplay = filteredCharts(group);

              return (
                <Accordion
                  key={group?.version}
                  expanded={isGroupExpanded}
                  onChange={() => toggleGroup(group?.version)}
                  disableGutters
                  className="vh-group-accordion"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="vh-group-summary"
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography className="vh-group-title">
                        {group?.visualization_name || "- -"}
                      </Typography>

                      <Typography className="vh-group-title">
                        {`V ${formatVersion(group?.toVersion)}`}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                        }}
                      >
                        ({chartsToDisplay?.length} change
                        {chartsToDisplay?.length !== 1 ? "s" : ""})
                      </Typography>
                    </Stack>
                    <Box>
                      {/* Version header */}
                      <Box className="vh-group-top">
                        <VersionPair group={group} />
                      </Box>

                      {/* Updated By */}
                      <Box
                        sx={{
                          marginTop: "12px",
                          marginBottom: "12px",
                        }}
                      >
                        <Label
                          text={`Updated by : ${group?.updatedBy || "--"}`}
                          fontSize="12px"
                          background="#ffffff"
                          display="inline-block"
                          padding="4px 7px"
                          borderRadius="3px"
                          color="#535862"
                        />
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails className="vh-group-details">
                    {/* Charts */}
                    <Box className="vh-charts">
                      {chartsToDisplay?.length === 0 ? (
                        <Box
                          sx={{
                            padding: "16px",
                            textAlign: "center",
                            color: "#999",
                            fontSize: "12px",
                          }}
                        >
                          No changes match the selected field filter
                        </Box>
                      ) : (
                        chartsToDisplay?.map((chart) => {
                          const chartExpanded = getChartExpanded(group, chart);
                          const isDeleted =
                            chart?.status === "Chart Deleted" ||
                            chart?.status === "Deleted";
                          const fieldsToDisplay = filteredFields(chart);

                          return (
                            <Accordion
                              key={chart?.chartUuid}
                              expanded={chartExpanded}
                              onChange={() =>
                                !isDeleted &&
                                toggleChart(group?.version, chart?.chartUuid)
                              }
                              disableGutters
                              className="vh-chart-accordion"
                            >
                              <AccordionSummary
                                expandIcon={
                                  !isDeleted ? <ExpandMoreIcon /> : null
                                }
                                className="vh-chart-summary"
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={0.8}
                                  minWidth={0}
                                >
                                  <Label
                                    text={chart?.chartName || "--"}
                                    lineHeight="1 !important"
                                    background="#F5FAFF"
                                    padding="4px 8px"
                                    borderRadius="3px"
                                  />

                                  <StatusBadge status={chart?.status} />
                                </Stack>
                              </AccordionSummary>

                              {fieldsToDisplay?.length > 0 && (
                                <AccordionDetails className="vh-chart-details">
                                  <TableContainer className="vh-table-container">
                                    <Table
                                      size="small"
                                      className="vh-change-table"
                                      stickyHeader
                                    >
                                      <TableHead>
                                        <TableRow>
                                          <TableCell className="vh-field-header">
                                            Field Path
                                          </TableCell>

                                          {chart?.status !== "Created" && (
                                            <TableCell className="vh-version-header vh-version-header-old">
                                              {`Version - ${formatVersion(group?.fromVersion)}`}
                                            </TableCell>
                                          )}

                                          <TableCell className="vh-version-header vh-version-header-new">
                                            {`Version - ${formatVersion(group?.toVersion)}`}
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>

                                      <TableBody>
                                        {fieldsToDisplay?.map(
                                          (field, index) => (
                                            <TableRow
                                              key={`${field?.fieldPath}-${index}`}
                                            >
                                              <TableCell className="vh-field-cell">
                                                {field?.fieldPath}
                                              </TableCell>

                                              {chart?.status !== "Created" && (
                                                <TableCell className="vh-value-cell">
                                                  {formatValue(
                                                    field[
                                                      group?.fromVersion
                                                      // group?.version
                                                    ],
                                                  )}
                                                </TableCell>
                                              )}

                                              <TableCell className="vh-value-cell">
                                                {formatValue(
                                                  field[
                                                    group?.toVersion

                                                    // group?.version > 0
                                                    //   ? group?.version - 1
                                                    //   : 0
                                                  ],
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ),
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </AccordionDetails>
                              )}
                            </Accordion>
                          );
                        })
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </Box>
      </Box>

      {err && (
        <CustomizedSnackbars
          severity="error"
          state={err}
          text={message}
          handleClose={() => setErr(false)}
        />
      )}
    </Dialog>
  );
};

export default VersionHistoryModal;
